import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { InputTextarea } from 'primereact/inputtextarea';
import { sendTelegramMessage } from '../../reducers/telegram/telegram-action';
import { ReactComponent as SendButton } from '../../assets/svgIcons/svgIcons/sendbuttonicon.svg';
import { ReactComponent as PlusIcon } from '../../svg/plus.svg';
import { ReactComponent as SmileIcon } from '../../svg/smile.svg';

import './input.css';

import {
  DOC_INPUT_EXTENTIONS,
  IMAGE_INPUT_EXTENTIONS,
  NO_OF_VIDEOS_AND_IMAGES,
  VIDEO_INPUT_EXTENTIONS,
} from '../../config/FileConstants';

import { showFilelimitWarning } from '../../reducers/enqChatSlice';
import { v4 as uuid } from 'uuid';
import { compressImg } from '../../utils/templateUtils';
import { useLayout } from '../../context/LayoutContext';
import data from '@emoji-mart/data';
import Picker from '@emoji-mart/react';
import { Controller, useForm } from 'react-hook-form';
import Record from '../WcaEnqChat/record';
import { API_GET_IMG_URL, API_UPLOAD_IMG } from '../../api/chat.service';
import { changeIsRecAudioSelect } from '../../reducers/telegram/telegram-slice';

const formatTime = (time) => {
  const minutes = Math.floor(time / 60);
  const seconds = time % 60;

  return `${minutes < 10 ? '0' : ''}${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
};

const Input = ({ file, setFileselected }) => {
  const dispatch = useDispatch();
  const { selectedBotConfigId, selectedContact } = useSelector((state) => state.telegram);

  const [error, setError] = useState(null);
  const [showOptions, setShowOptions] = useState(false);
  const [openEmoji, setOpenEmoji] = useState(false);

  const [blobURL, setblobUrl] = useState(null);
  const [blobData, setBlobData] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [timer, setTimer] = useState(0);
  const [isDisbaleSend, setIsDisableSend] = useState(false);

  const plusWrapperRef = useRef(null);
  const { toast } = useLayout(null);
  const divFileAndPhotoRef = useRef(null);
  const textAreaRef = useRef(null);

  const defaultValues = { content: '' };
  const { control, reset, handleSubmit, setValue, getValues } = useForm({ defaultValues });

  useEffect(() => {
    let intervalId;

    if (isRecording) {
      intervalId = setInterval(() => {
        setTimer((prevTimer) => prevTimer + 1);
      }, 1000);
    } else {
      clearInterval(intervalId);
      setTimer(0);
    }

    return () => {
      clearInterval(intervalId);
    };
  }, [isRecording]);

  /* ================= Outside Click Close ================= */
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (plusWrapperRef.current && !plusWrapperRef.current.contains(e.target)) {
        setShowOptions(false);
      }
    };

    document.addEventListener('click', handleOutsideClick);
    return () => document.removeEventListener('click', handleOutsideClick);
  }, []);

  /* ================= Send Message ================= */
  const handleSendMessage = async () => {
    const message = getValues('content');
    if (!message.trim() || !selectedContact || !selectedBotConfigId) return;

    try {
      setError(null);

      await dispatch(
        sendTelegramMessage({
          botConfigId: selectedBotConfigId,
          payload: {
            type: 'message',
            message: {
              chat_id: selectedContact.chatId,
              text: message.trim(),
            },
          },
        }),
      ).unwrap();

      reset();
      setOpenEmoji(false);
    } catch (err) {
      setError('Failed to send message');
    }
  };

  /* ================= Enter to Send ================= */
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  /* ================= Upload Logic (UNCHANGED) ================= */
  const handleUpload = async (e, type) => {
    const filesFromInput = e.target.files;
    let fileArray = Array.from(filesFromInput).slice(0, 5);
    let files = [];
    let limit = 0;

    if (e?.files?.length > NO_OF_VIDEOS_AND_IMAGES) {
      dispatch(
        showFilelimitWarning({
          filesLength: e?.files?.length,
          filesLengthWarning: true,
        }),
      );
    }

    for (const x of fileArray) {
      const unique_id = uuid();
      limit++;
      if (limit > NO_OF_VIDEOS_AND_IMAGES) break;

      try {
        await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve();
          reader.onerror = () => reject();
          reader.readAsArrayBuffer(x);
        });

        if (type === 'media') {
          if (x.type.includes('image')) {
            const compressed = await compressImg(x);
            files.push({
              id: unique_id.slice(0, 8),
              data: new File([compressed], compressed.name, {
                type: compressed.type,
              }),
              type: 'image',
            });
          } else {
            files.push({
              id: unique_id.slice(0, 8),
              data: x,
              type: 'video',
            });
          }
        } else {
          files.push({
            id: unique_id.slice(0, 8),
            data: x,
            type: 'document',
          });
        }
      } catch {
        toast.current.show({
          severity: 'error',
          detail: `Error processing file: ${x.name}`,
        });
      }
    }

    setFileselected(files);
    setShowOptions(false);
  };

  /* ================= Options UI ================= */
  function OptionsComponenet() {
    return (
      <div
        ref={divFileAndPhotoRef}
        className="flex __br justify-content-center align-items-center __sendOptionsAfter"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-column justify-content-center align-items-center">
          {/* Photos / Videos */}
          <div className="__itemDiv">
            <input
              type="file"
              style={{ display: 'none' }}
              id="media"
              onChange={(e) => handleUpload(e, 'media')}
              accept={`${IMAGE_INPUT_EXTENTIONS}, ${VIDEO_INPUT_EXTENTIONS}`}
            />

            <label htmlFor="media" className="cursor-pointer flex align-items-center gap-1">
              <i className="pi pi-paperclip" />
              <span>Photos & Videos</span>
            </label>
          </div>

          {/* Documents */}
          <div className="__itemDiv">
            <input
              type="file"
              style={{ display: 'none' }}
              id="Documents"
              onChange={(e) => handleUpload(e, 'doc')}
              // multiple
              accept={`${DOC_INPUT_EXTENTIONS}`}
            />

            <label htmlFor="Documents" className="cursor-pointer flex align-items-center gap-1">
              <i className="pi pi-file" />
              <span>Documents</span>
            </label>
          </div>
        </div>
      </div>
    );
  }

  const handleEmojiSelect = (emoji) => {
    const native = emoji?.native;
    const input = textAreaRef.current;
    const value = getValues('content') || '';

    const start = input?.selectionStart ?? value.length;
    const end = input?.selectionEnd ?? value.length;

    const newValue = value.substring(0, start) + native + value.substring(end);

    setValue('content', newValue, { shouldDirty: true, shouldTouch: true });

    setTimeout(() => {
      input?.setSelectionRange(start + native.length, start + native.length);
      input?.focus();
    }, 0);

    setOpenEmoji(true);
  };
  const handelAudioSend = async () => {
    setIsDisableSend(true);
    let name = Math.floor(Math.random() * 10000000) + '.mpeg';
    var file = new File([blobData], `${name}`, { type: 'audio/mpeg' });
    try {
      if (blobData) {
        const resp = await API_GET_IMG_URL();
        const uploadUrl = resp.data.src;
        const fileUrl = resp.data.url;

        if (uploadUrl) {
          await API_UPLOAD_IMG(uploadUrl, file, 'audio/mpeg');
        }

        const payload = {
          type: 'audio',
          message: {
            chat_id: selectedContact.chatId,
            audio: fileUrl,
          },
        };
        console.log('payload', payload);

        await dispatch(
          sendTelegramMessage({
            botConfigId: selectedBotConfigId,
            chatId: selectedContact.chatId,
            payload,
          }),
        )
          .unwrap()
          .then(() => {
            reset();
            setblobUrl(null);
            setBlobData(null);
            dispatch(changeIsRecAudioSelect(false));
            setIsDisableSend(false);
          });
      }
    } catch (error) {
      toast.current.show({
        severity: 'error',
        detail: error?.response?.message,
      });
    }
  };

  const handleRecAudioSend = () => {
    if (blobURL !== null) {
      handelAudioSend();
    }
  };

  return (
    <div className="__inputContainer">
      {showOptions && <OptionsComponenet />}
      {error && <div className="error-message">{error}</div>}

      <div className="inputMainRow relative">
        {openEmoji && (
          <div className="absolute border-1" style={{ bottom: 90, zIndex: 3 }}>
            <Picker
              data={data}
              onEmojiSelect={handleEmojiSelect}
              onClickOutside={() => setOpenEmoji(false)}
            />
          </div>
        )}
        <div
          className="__mb"
          onClick={(e) => {
            e.stopPropagation();
            setOpenEmoji((prev) => !prev);
          }}
        >
          <span className="__Items p-2">
            <SmileIcon />
          </span>
        </div>
        {/* Plus Icon */}
        <div ref={plusWrapperRef} className="__EmojiItems">
          <div
            className={`cursor-pointer p-2 ${showOptions ? '__backC' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              setShowOptions((prev) => !prev);
            }}
          >
            <PlusIcon />
          </div>
        </div>

        {/* Input */}
        <form
          className="w-full md:w-9 relative flex-1 flex align-items-end gap-2"
          onSubmit={handleSubmit(handleSendMessage)}
        >
          <div className="inputBox ">
            <Controller
              name="content"
              control={control}
              render={({ field }) => (
                <InputTextarea
                  // onPaste={handlePaste}
                  id={field.content}
                  {...field}
                  ref={textAreaRef}
                  className="w-full h-auto"
                  rows={3}
                  cols={30}
                  autoResize
                  placeholder={!isRecording && !blobURL ? 'Enter your message...' : ''}
                  // placeholder={'Enter your message...'}
                  onKeyDown={handleKeyDown}
                />
              )}
            />

            <div
              className={`absolute sendbutton flex justify-content-center align-items-center `}
              style={{ bottom: '10px', right: '5px' }}
            >
              {blobData && (
                <div className="flex">
                  <audio
                    src={blobURL}
                    id="audioElement"
                    className=""
                    style={{ width: '225px' }}
                    controls
                  ></audio>
                  {!isDisbaleSend && (
                    <button
                      icon="pi pi-trash"
                      className="mx-1 __delBtn  "
                      onClick={() => {
                        dispatch(changeIsRecAudioSelect(false));
                        setBlobData(null);
                        setblobUrl(null);
                      }}
                    >
                      <i class="pi pi-trash"></i>
                    </button>
                  )}
                </div>
              )}

              {isRecording ? (
                <div className="mr-1 __audioGif flex justify-content-center align-items-center">
                  <div className="mr-1">
                    <img src="./assets/Audio.gif" />
                    <img src="./assets/Audio.gif" />
                    <img src="./assets/Audio.gif" />
                    <img src="./assets/Audio.gif" />
                  </div>

                  <div>
                    <span>{formatTime(timer)}</span>
                  </div>
                </div>
              ) : (
                <button
                  type={blobURL != null ? '' : 'submit'}
                  onClick={handleRecAudioSend}
                  disabled={isDisbaleSend}
                >
                  {isDisbaleSend ? (
                    <i
                      class="pi pi-spin pi-spinner"
                      style={{ color: 'black', width: '1.5rem' }}
                    ></i>
                  ) : (
                    <SendButton />
                  )}
                </button>
              )}
            </div>
          </div>

          <div>
            <Record
              setblobUrl={setblobUrl}
              setBlobData={setBlobData}
              isRecording={isRecording}
              setIsRecording={setIsRecording}
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default Input;
